import * as React from 'react';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { IProjectCompanyOption } from '@company/projectCompany/projectCompany.models';
import { Form, FormSelect, Grid, IItemResponse, requiredRule, UseFormReturn } from '@gcl/react-core';
import { LabelWithMessage } from '@common/LabelWithMessage';

import { FETCH_QUERIES_NAME } from '@common/constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import usePrompt from '@common/prompt.hooks';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import {
  EDesignModificationFormKeys,
  IDesignModificationReassignCheckerFormState,
  IProjectDesignModification,
  IReassignCheckerForProjectDesignModificationRequest,
} from '@designModification/designModification.models';
import { useFetchCheckerCompanyOptions } from '@company/projectCompany/projectCompany.queries';
import { useFetchTeamsOptionsByCompanyId } from '@projectTeam/projectTeam.queries';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { AxiosErrorResponse } from '@core/error.models';

export interface IDesignModificationReassignCheckerFormProps {
  projectId: number;
  designModification?: IProjectDesignModification;
  useFormMethods: UseFormReturn;
  mutationReassignChecker: UseMutationResult<
    IItemResponse<IProjectDesignModification>,
    AxiosErrorResponse,
    IReassignCheckerForProjectDesignModificationRequest,
    unknown
  >;
}

export const DesignModificationReassignCheckerForm: React.FunctionComponent<
  IDesignModificationReassignCheckerFormProps
> = ({ projectId, designModification, useFormMethods, mutationReassignChecker }) => {
  const queryClient = useQueryClient();

  const selectedCheckerCompany: IProjectCompanyOption | undefined = useFormMethods.watch(
    EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY,
  );

  const {
    formState: { isDirty },
  } = useFormMethods;

  const { data: checkerCompanyOptions } = useFetchCheckerCompanyOptions({ shouldConvertOptions: false, isDirty })({
    projectId,
  });

  const { data: checkerTeamOptions } = useFetchTeamsOptionsByCompanyId({
    shouldConvertOptions: false,
  })(
    { projectId, companyId: selectedCheckerCompany?.id ?? designModification?.nominatedChecker?.projectCompanyId ?? 0 },
    isPositiveNumber(selectedCheckerCompany?.id ?? designModification?.nominatedChecker?.projectCompanyId),
  );

  const {
    isSuccess: isSuccessReassignChecker,
    isError: isErrorReassignChecker,
    error: errorReassignChecker,
    reset: resetReassignChecker,
  } = mutationReassignChecker;

  React.useEffect(() => {
    if (!isDirty) return;
    useFormMethods.setValue(EDesignModificationFormKeys.NOMINATED_CHECKER, undefined);
  }, [JSON.stringify(selectedCheckerCompany)]);

  const getDefaultValues = (
    designModificationLocal?: IProjectDesignModification,
    checkerCompanyOptionsLocal?: IProjectCompanyOption[],
    checkerTeamOptionsLocal?: IProjectTeam[],
  ) => {
    const nominatedCheckerCompany: IProjectCompanyOption | null =
      checkerCompanyOptionsLocal?.find((c) => c.id === designModificationLocal?.nominatedChecker?.projectCompanyId) ??
      null;

    const nominatedChecker =
      checkerTeamOptionsLocal?.find((t) => t.id === designModificationLocal?.nominatedChecker.id) ?? null;

    const state: IDesignModificationReassignCheckerFormState = {
      [EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY]: nominatedCheckerCompany,
      [EDesignModificationFormKeys.NOMINATED_CHECKER]: nominatedChecker,
    };
    return state;
  };

  return (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessReassignChecker,
            message: 'Update project team for master schedule success.',
            reset: resetReassignChecker,
          },
        ]}
        callbackAfterToasted={() => {
          queryClient.invalidateQueries([
            projectId,
            designModification?.id,
            FETCH_QUERIES_NAME.DESIGN_MODIFICATION_DETAIL,
          ]);
        }}
      />
      <ApiErrorDisplay
        setFormError={useFormMethods.setError}
        errors={[
          { error: errorReassignChecker, genericError: 'Re-assign Checker failed', reset: resetReassignChecker },
        ]}
      />
      <Form
        defaultValues={{
          callback: () => {
            const state = getDefaultValues(
              designModification,
              checkerCompanyOptions?.map((c) => {
                return { ...c.data, ...c } as IProjectCompanyOption;
              }),
              checkerTeamOptions?.map((c) => {
                return { ...c.data, ...c } as IProjectTeam;
              }),
            );
            return state;
          },
          deps: [designModification, checkerCompanyOptions, checkerTeamOptions],
        }}
        enableUnsavedChangesPrompt
        unsavedChangesPromptProps={{
          usePrompt,
        }}
        useFormMethods={useFormMethods}
        render={(formFieldMethods) => {
          return (
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item xs={12} md={6}>
                <LabelWithMessage
                  labelFor={EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY}
                  message="Can only be changed by EM/TWC"
                  text="Nominated checker"
                  required
                />

                <FormSelect
                  options={checkerCompanyOptions || []}
                  displayFieldName="name"
                  formFieldMethods={formFieldMethods}
                  id={EDesignModificationFormKeys.NOMINATED_CHECKER_COMPANY}
                  clearable={false}
                  allowSearch={false}
                  rules={requiredRule}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LabelWithMessage
                  labelFor={EDesignModificationFormKeys.NOMINATED_CHECKER}
                  message="Can only be changed by EM/TWC"
                  text="Nominated checker team"
                  required
                />

                <FormSelect
                  options={checkerTeamOptions || []}
                  displayFieldName="teamName"
                  formFieldMethods={formFieldMethods}
                  id={EDesignModificationFormKeys.NOMINATED_CHECKER}
                  disabled={!selectedCheckerCompany}
                  clearable={false}
                  allowSearch={false}
                  rules={requiredRule}
                />
              </Grid>
            </Grid>
          );
        }}
      />
    </>
  );
};
